import { PageBackLink } from "@/components/page-back-link"
import { ListSearchFilters } from "@/components/list-search-filters"
import { ProductFiltersSchema } from "@optimist/schemas"
import { usePortalAuth } from "@/features/authz/hooks/use-portal-auth"
import { createPortal } from "react-dom"
import { useEffect, useRef, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import type { DataGridColumn } from "@/components/data-grid/data-grid"
import { MoreVertical } from "lucide-react"
import { z } from "zod"

import { StatusBadge } from "@/components/status-badge"
import { DataGrid } from "@/components/data-grid"
import { Pagination } from "@/components/pagination"
import { ErrorBanner, ErrorBoundary } from "@/components/error-boundary"
import { PageBreadcrumbs, PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { CreateProductDialog } from "@/features/products/components/create-product-dialog"
import type { Product, ProductAvailability } from "@/features/products/hooks/use-product-mutations"
import { api } from "@/lib/api"
import { getRowIndexProps } from "@/lib/pagination"
import { queryKeys } from "@/lib/query-keys"
import { formatDateTime } from "@/lib/utils"

interface ProductRow {
	id: string
	ndc: string
	sku: string | null
	productCode: string | null
	csn: string | null
	name: string
	form: string | null
	description: string | null
	dose: string | null
	daysSupply: number | null
	unitOfMeasure: string | null
	strength: string | null
	isActive: boolean
	createdAt: string
	availability: ProductAvailability
	offerings: Array<{ id: string }>
	variants: Array<{ id: string }>
}

interface ProductListResponse {
	data: ProductRow[]
	meta: {
		page: number
		limit: number
		total: number
		totalPages: number
	}
}

const ProductListSearchSchema = ProductFiltersSchema.extend({ duplicateProductId: z.uuid().optional() })

function buildDuplicateProductValues(product: ProductRow): Partial<Product> {
	const suffix = Math.random().toString(16).slice(2, 8).toUpperCase()
	return {
		ndc: `${product.ndc}-copy-${suffix}`,
		sku: product.sku ? `${product.sku}-copy-${suffix}` : `copy-${suffix}`,
		productCode: product.productCode,
		csn: product.csn,
		name: `Copy of ${product.name}`,
		description: product.description,
		dose: product.dose,
		daysSupply: product.daysSupply,
		unitOfMeasure: product.unitOfMeasure,
		form: product.form,
		strength: product.strength,
		isActive: true,
	}
}

function ProductActionsMenu({
	productId,
	onDuplicate,
}: {
	productId: string
	onDuplicate: (productId: string) => void
}) {
	const buttonRef = useRef<HTMLButtonElement | null>(null)
	const menuRef = useRef<HTMLDivElement | null>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

	const updatePosition = () => {
		const button = buttonRef.current
		if (!button) return

		const rect = button.getBoundingClientRect()
		setPosition({
			top: rect.bottom + 8,
			left: rect.left,
		})
	}

	useEffect(() => {
		if (!isOpen) return

		updatePosition()

		const handleBoundaryChange = () => updatePosition()
		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target
			if (!(target instanceof Node)) return
			if (buttonRef.current?.contains(target)) return
			if (menuRef.current?.contains(target)) return
			setIsOpen(false)
			setPosition(null)
		}

		window.addEventListener("resize", handleBoundaryChange)
		window.addEventListener("scroll", handleBoundaryChange, true)
		window.addEventListener("pointerdown", handlePointerDown)

		return () => {
			window.removeEventListener("resize", handleBoundaryChange)
			window.removeEventListener("scroll", handleBoundaryChange, true)
			window.removeEventListener("pointerdown", handlePointerDown)
		}
	}, [isOpen])

	const closeMenu = () => {
		setIsOpen(false)
		setPosition(null)
	}

	return (
		<>
			<Button size="icon" variant="secondary"
				ref={buttonRef}
				type="button"
				aria-haspopup="menu"
				aria-expanded={isOpen}
				aria-label="Open actions"
				className="w-8 justify-center"
				onClick={(event) => {
					event.stopPropagation()
					setIsOpen((current) => !current)
				}}
			>
				<MoreVertical className="size-4" />
			</Button>

			{isOpen && position && typeof document !== "undefined"
				? createPortal(
						<div
							ref={menuRef}
							className="fixed z-50 min-w-28 rounded-md border bg-popover p-1 shadow-lg shadow-black/10"
							style={{
								top: position.top,
								left: position.left,
							}}
							onClick={(event) => event.stopPropagation()}
						>
							<button
								type="button"
								className="w-full rounded-sm px-2 py-1 text-left text-body hover:bg-accent"
								onClick={(event) => {
									event.stopPropagation()
									closeMenu()
									onDuplicate(productId)
								}}
							>
								Duplicate
							</button>
						</div>,
						document.body
				  )
				: null}
		</>
	)
}

export const Route = createFileRoute("/dashboard/products/")({
	validateSearch: (search) => ProductListSearchSchema.parse(search),
	component: ProductsPage,
})

function ProductsPage() {
	const auth = usePortalAuth()
	const canRead = auth.data !== undefined && auth.data.permissions.includes("products.read")
	const canManage = auth.data !== undefined && auth.data.permissions.includes("products.manage")
	const navigate = useNavigate({ from: Route.fullPath })
	const search = Route.useSearch()
	const { duplicateProductId, ...filters } = search
	const { page, limit } = filters
	const [isCreateOpen, setIsCreateOpen] = useState(false)
	const [prefillValues, setPrefillValues] = useState<Partial<Product> | null>(null)
	const [activeDuplicateProductId, setActiveDuplicateProductId] = useState<string | null>(null)

	const { data, isLoading, error, refetch } = useQuery({
		enabled: canRead,
		queryKey: queryKeys.products.list(filters),
		queryFn: () => api.get<ProductListResponse>("/products", { params: filters }),
	})

	useEffect(() => {
		if (!canManage || !duplicateProductId) {
			setActiveDuplicateProductId(null)
			setPrefillValues(null)
			return
		}

		if (!data?.data) return
		if (duplicateProductId === activeDuplicateProductId) return

		const source = data.data.find((product) => product.id === duplicateProductId)
		if (!source) return

		setActiveDuplicateProductId(duplicateProductId)
		setPrefillValues(buildDuplicateProductValues(source))
		setIsCreateOpen(true)
	}, [canManage, data?.data, duplicateProductId, activeDuplicateProductId])

	const clearDuplicateSearchParam = () => {
		void navigate({
			search: (prev) => ({
				...prev,
				duplicateProductId: undefined,
			}),
		})
	}

	const closeCreateDialog = () => {
		setIsCreateOpen(false)
		setPrefillValues(null)
		setActiveDuplicateProductId(null)
		clearDuplicateSearchParam()
	}

	const onCreateClick = () => {
		if (duplicateProductId) {
			clearDuplicateSearchParam()
		}
		setPrefillValues(null)
		setActiveDuplicateProductId(null)
		setIsCreateOpen(true)
	}

	const onDuplicate = (productId: string) => {
		navigate({
			search: (prev) => ({
				...prev,
				duplicateProductId: productId,
			}),
		})
	}

	const columnDefs: DataGridColumn<ProductRow>[] = [
		{
			field: "name",
			headerName: "Product",
		},
		{
			field: "productCode",
			headerName: "Code",
			render: (row) => row.productCode ?? row.ndc ?? "-",
		},
		{
			field: "form",
			headerName: "Form",
			render: (row) => row.form ?? "-",
		},
		{
			headerName: "Offerings",
			render: (row) => row.offerings.length ?? 0,
		},
		{
			headerName: "Variants",
			render: (row) => row.variants.length ?? 0,
		},
		{
			field: "isActive",
			headerName: "Status",
			render: (row) => (
				<StatusBadge variant={row.isActive ? "success" : "secondary"}>
					{row.isActive ? "Active" : "Inactive"}
				</StatusBadge>
			),
		},
		{
			headerName: "Checkout",
			render: (row) => {

				if (!row) return "-"
				return (
                    <div className="flex flex-col gap-1">
                        <StatusBadge variant={row.availability.checkoutAvailable ? "success" : "warning"}>
							{row.availability.checkoutAvailable ? "Available" : "Setup required"}
						</StatusBadge>
                        {!row.availability.checkoutAvailable ? (
							<span className="text-chip text-muted-foreground">
								{row.availability.reasons[0]?.replace(/_/g, " ") ?? "Setup needed"}
							</span>
						) : null}
                    </div>
                )
			},
		},
		{
			field: "createdAt",
			headerName: "Created",
			render: (row) => (row.createdAt ? formatDateTime(row.createdAt) : "-"),
		},
		{
			headerName: "Actions",
			render: (row) => {
				if (!row || !canManage) return "-"
				return (
					<ProductActionsMenu productId={row.id} onDuplicate={onDuplicate} />
				)
			},
		},
	]

	if (auth.isPending) return <p>Loading access…</p>
	if (auth.isError) return <ErrorBanner title="Unable to load access" message="Your catalog access could not be checked." onRetry={() => void auth.refetch()} />
	if (!canRead) return <ErrorBanner title="Catalog access required" message="You do not have permission to view the product catalog." />

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
					<PageHeader back={<PageBackLink to="/dashboard" label="Back to dashboard" />}
						breadcrumbs={
							<PageBreadcrumbs
								items={[
									<Link key="dashboard" to="/dashboard">Dashboard</Link>,
									<span key="products">Products</span>,
								]}
							/>
						}
						title="Products"
					/>
				<div className="flex items-center gap-2 lg:pt-10">
					{canManage ? <Button onClick={onCreateClick}>Create product</Button> : null}
				</div>
			</div>

			<ListSearchFilters
				key={JSON.stringify([filters.search, filters.isActive])}
				title="Search and filter products"
				placeholder="Product ID, name, code, NDC, or SKU"
				search={filters.search}
				status={filters.isActive}
				statuses={[{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }]}
				onApply={(next) => navigate({ search: ProductListSearchSchema.parse({ search: next.search, isActive: next.status, page: 1, limit }) })}
				onClear={() => navigate({ search: { page: 1, limit } })}
			/>

			{error ? (
				<ErrorBanner
					title="Failed to load products"
					message="The product catalog could not be loaded."
					onRetry={() => {
						void refetch()
					}}
				/>
			) : (
				<ErrorBoundary
					fallback={<ErrorBanner title="Products unavailable" message="The product table could not be rendered." />}
				>
					<DataGrid<ProductRow>
						rowData={data?.data ?? []}
						columnDefs={columnDefs}
						loading={isLoading}
						{...getRowIndexProps({ meta: data?.meta ?? null, page, limit, mode: "descending" })}
						onRowClicked={(row: ProductRow) => {
							navigate({ to: "/dashboard/products/$productId", params: { productId: row.id } })
						}}
					/>
				</ErrorBoundary>
			)}

			{data?.meta ? (
				<Pagination
					page={data.meta.page}
					limit={data.meta.limit}
					total={data.meta.total}
					totalPages={data.meta.totalPages}
					onPageChange={(nextPage) => {
						navigate({ search: (prev) => ({ ...prev, page: nextPage }) })
					}}
					onLimitChange={(nextLimit) => {
						navigate({ search: (prev) => ({ ...prev, page: 1, limit: nextLimit }) })
					}}
				/>
			) : null}

			<CreateProductDialog
				open={canManage && isCreateOpen}
				onOpenChange={(next) => {
					if (next) {
						setIsCreateOpen(true)
						return
					}
					closeCreateDialog()
				}}
				initialValues={prefillValues}
			/>
		</div>
	)
}
